import {
    Box,
    Chip,
    Container,
    Grid,
    Skeleton,
    Stack,
    Typography
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    deleteDocument,
    getAllDocuments,
    saveDocument
} from '../../../data/api/document/DocumentRoutes';
import DocumentContainer from '../../documents/DocumentContainer';
import { useForm } from 'react-hook-form';
import { Confirm, Notify } from 'notiflix';
import useIndex from '../../../hooks/useIndex';
import { LoadingButton } from '@mui/lab';
import validErrors from '../../../hooks/validErrors';

const Files = () => {
    const { token } = useIndex();
    const { register, handleSubmit, reset } = useForm();

    const [submit, setSubmit] = useState(false);
    const [documents, setDocuments] = useState();
    const [fileLoaded, setFileLoaded] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setDocuments(null);

        try {
            const { data } = await getAllDocuments();
            setDocuments(data?.data);
        } catch (error) {
            validErrors(error);
        }
    };

    const onSubmit = async () => {
        if (!fileLoaded) {
            Notify.failure('Debes seleccionar un archivo.');
            return;
        }

        setSubmit(true);

        try {
            const frm = new FormData();
            frm.append('file', fileLoaded);

            const { data } = await saveDocument(token, frm);

            if (data?.success) {
                Notify.success(data?.message);
                loadData();
                setFileLoaded(null);
                reset();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
        }
    };

    const onDeleteAlert = (uuid) => {
        Confirm.show(
            'Eliminar documento',
            'El archivo será eliminado permanentemente. Esta acción no se puede restaurar.',
            'Eliminar',
            'Cancelar',
            async () => {
                try {
                    const { data } = await deleteDocument(token, uuid);

                    if (data?.success) {
                        Notify.success(data?.message);
                        loadData();
                    }
                } catch (error) {
                    validErrors(error);
                }
            },
            () => { }
        );
    };

    const fileSizeKb = fileLoaded?.size
        ? `${(fileLoaded.size / 1024).toFixed(2)} KB`
        : '0 KB';

    return (
        <PageContainer maxWidth={false}>
            <Header>
                <Box className="icon">
                    <FolderOpenOutlinedIcon />
                </Box>

                <Box>
                    <Typography className="title">
                        Documentos públicos
                    </Typography>

                    <Typography className="subtitle">
                        Administra los archivos visibles para los usuarios y visitantes de Fenabocol.
                    </Typography>
                </Box>
            </Header>

            <MainCard>
                <SectionHeader>
                    <Box>
                        <Typography className="section-title">
                            Documentos cargados
                        </Typography>

                        <Typography className="section-subtitle">
                            Lista de archivos públicos disponibles actualmente.
                        </Typography>
                    </Box>

                    <Chip
                        size="small"
                        label={`Total: ${documents?.length ?? 0}`}
                        sx={{
                            fontWeight: 900,
                            color: '#10285f',
                            background: 'rgba(214, 170, 58, 0.18)',
                            border: '1px solid rgba(214, 170, 58, 0.34)',
                        }}
                    />
                </SectionHeader>

                <DocumentsBox>
                    {!documents ? (
                        <SkeletonGrid>
                            {[1, 2, 3, 4].map((item) => (
                                <SkeletonCard key={item}>
                                    <Skeleton variant="rounded" height={96} width="100%" />
                                    <Skeleton variant="text" width="80%" />
                                </SkeletonCard>
                            ))}
                        </SkeletonGrid>
                    ) : documents?.length > 0 ? (
                        <DocumentContainer
                            documents={documents}
                            edit={true}
                            actionDelete={onDeleteAlert}
                        />
                    ) : (
                        <EmptyState>
                            <InsertDriveFileOutlinedIcon />
                            <Typography className="empty-title">
                                No hay documentos
                            </Typography>
                            <Typography className="empty-text">
                                Agrega el primer documento público usando el formulario inferior.
                            </Typography>
                        </EmptyState>
                    )}
                </DocumentsBox>

                <StyledDivider>
                    <span>Agregar documento</span>
                </StyledDivider>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <UploadSection>
                        <UploadInput
                            type="file"
                            id="upload_file"
                            name="upload_file"
                            {...register('file')}
                            onChange={(e) => setFileLoaded(e.target.files[0])}
                        />

                        <UploadFile
                            htmlFor="upload_file"
                            className={fileLoaded ? 'success' : ''}
                        >
                            <CloudUploadOutlinedIcon className="main-icon" />

                            <Typography className="upload-title">
                                {fileLoaded ? 'Archivo seleccionado' : 'Seleccionar archivo'}
                            </Typography>

                            <Typography className="upload-subtitle">
                                Haz clic para buscar un documento
                            </Typography>
                        </UploadFile>

                        <FileDescription>
                            <Box className="file-header">
                                <InsertDriveFileOutlinedIcon />

                                <Box>
                                    <Typography className="file-title">
                                        Detalle del archivo
                                    </Typography>

                                    <Typography className="file-subtitle">
                                        Información del documento seleccionado
                                    </Typography>
                                </Box>
                            </Box>

                            <InfoList>
                                <li>
                                    <span>Nombre:</span>
                                    <strong>{fileLoaded?.name ?? 'Sin archivo seleccionado'}</strong>
                                </li>

                                <li>
                                    <span>Peso:</span>
                                    <strong>{fileSizeKb}</strong>
                                </li>

                                <li>
                                    <span>Tipo:</span>
                                    <strong>{fileLoaded?.type || 'No disponible'}</strong>
                                </li>

                                <li>
                                    <span>Fecha:</span>
                                    <strong>{new Date().toLocaleDateString()}</strong>
                                </li>
                            </InfoList>
                        </FileDescription>
                    </UploadSection>

                    <LoadingButton
                        fullWidth
                        type="submit"
                        loading={submit}
                        loadingPosition="start"
                        startIcon={<CloudUploadOutlinedIcon />}
                        variant="contained"
                        disabled={!fileLoaded}
                        sx={{
                            mt: 2,
                            borderRadius: '14px',
                            py: 1.1,
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                            boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                            },
                            '&.Mui-disabled': {
                                background: 'rgba(15, 34, 77, 0.12)',
                                color: 'rgba(15, 34, 77, 0.36)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <span>Subir documento</span>
                    </LoadingButton>
                </form>
            </MainCard>
        </PageContainer>
    );
};

export default Files;

const PageContainer = styled(Container)`
    padding: 1.2rem !important;
    background: #f5f7fb;
    min-height: 100%;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1rem;

    .icon {
        width: 46px;
        height: 46px;
        border-radius: 15px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.3);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
            font-size: 1.55rem;
        }
    }

    .title {
        color: #10285f;
        font-size: 1.65rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.18rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        .title {
            font-size: 1.35rem;
        }

        .subtitle {
            font-size: 0.82rem;
        }
    }
`;

const MainCard = styled.div`
    padding: 0.8rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);

    @media (max-width: 600px) {
        padding: 0.85rem;
        border-radius: 18px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    .section-title {
        color: #10285f;
        font-size: 1.05rem;
        font-weight: 900;
    }

    .section-subtitle {
        margin-top: 0.12rem;
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.82rem;
        font-weight: 600;
    }
`;

const DocumentsBox = styled.div`
    padding: 0.65rem;
    border-radius: 16px;
    background: rgba(16, 40, 95, 0.025);
    border: 1px dashed rgba(15, 34, 77, 0.14);
    min-height: 120px;

    /* Compacta las cards internas del DocumentContainer */
    & > div {
        gap: 0.75rem !important;
    }

    a,
    .document-card,
    [class*="card"],
    [class*="Card"] {
        min-height: 190px !important;
        max-height: 210px !important;
        padding: 0.85rem !important;
        border-radius: 16px !important;
    }

    img,
    svg {
        max-width: 64px !important;
        max-height: 64px !important;
    }

    h1,
    h2,
    h3,
    h4,
    p {
        margin-top: 0.25rem !important;
        margin-bottom: 0.25rem !important;
    }

    p {
        font-size: 0.82rem !important;
        line-height: 1.25 !important;
    }

    a {
        font-size: 0.82rem !important;
    }

    @media (max-width: 600px) {
        padding: 0.5rem;

        a,
        .document-card,
        [class*="card"],
        [class*="Card"] {
            min-height: 165px !important;
            max-height: 185px !important;
            padding: 0.7rem !important;
        }

        img,
        svg {
            max-width: 52px !important;
            max-height: 52px !important;
        }
    }
`;

const SkeletonGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
`;

const SkeletonCard = styled.div`
    width: 150px;
    padding: 0.75rem;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(15, 34, 77, 0.08);
`;

const EmptyState = styled.div`
    min-height: 150px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    svg {
        font-size: 2.2rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.1rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.86rem;
    }
`;

const StyledDivider = styled.div`
    margin: 1rem 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 34, 77, 0.13);
    }

    span {
        padding: 0.35rem 0.9rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }
`;

const UploadSection = styled.div`
    display: grid;
    grid-template-columns: 190px minmax(0, 1fr);
    gap: 1rem;
    align-items: stretch;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const UploadInput = styled.input`
    width: 0;
    height: 0;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: -1;
`;

const UploadFile = styled.label`
    min-height: 170px;
    border: 1.5px dashed rgba(15, 34, 77, 0.18);
    border-radius: 18px;
    position: relative;
    padding: 1rem;
    text-align: center;
    color: rgba(15, 34, 77, 0.58);
    background: #fff;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.35rem;

    cursor: pointer;
    transition: 0.25s ease;

    .main-icon {
        font-size: 3.4rem;
        color: #d6aa3a;
        margin-bottom: 0.25rem;
    }

    .upload-title {
        color: #10285f;
        font-size: 0.98rem;
        font-weight: 900;
    }

    .upload-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.78rem;
        font-weight: 600;
    }

    &:hover {
        border-color: rgba(214, 170, 58, 0.75);
        background: rgba(214, 170, 58, 0.08);
        transform: translateY(-1px);
    }

    &.success {
        border-color: rgba(46, 125, 50, 0.45);
        background: rgba(46, 125, 50, 0.06);

        .main-icon {
            color: #2e7d32;
        }
    }
`;

const FileDescription = styled.div`
    padding: 1rem;
    border-radius: 18px;
    background: rgba(16, 40, 95, 0.035);
    border: 1px solid rgba(15, 34, 77, 0.08);

    .file-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.85rem;

        svg {
            color: #d6aa3a;
            font-size: 1.4rem;
            flex-shrink: 0;
        }
    }

    .file-title {
        color: #10285f;
        font-size: 0.98rem;
        font-weight: 900;
    }

    .file-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.78rem;
        font-weight: 600;
    }
`;

const InfoList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        display: grid;
        grid-template-columns: 90px minmax(0, 1fr);
        gap: 0.5rem;
        padding: 0.45rem 0;
        border-bottom: 1px solid rgba(15, 34, 77, 0.08);

        &:last-child {
            border-bottom: none;
        }
    }

    span {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.78rem;
        font-weight: 800;
        text-transform: uppercase;
    }

    strong {
        color: #10285f;
        font-size: 0.86rem;
        font-weight: 900;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    @media (max-width: 500px) {
        li {
            grid-template-columns: 1fr;
            gap: 0.1rem;
        }
    }
`;